import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Plus, ClipboardList, FileText, Eye, Check, X, Pencil, Trash2, AlertTriangle, ChevronsUpDown, Pill } from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { Attachments } from '../components/Attachments';
import { fmtDate } from '../lib/datetime';
import { useArticleUnits } from '../hooks/useArticleUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = ['unidad', 'comprimido', 'ampolla', 'frasco', 'caja', 'sobre', 'ml', 'mg', 'g', 'kg', 'litro'];

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  // Permisos por defecto de supervisor
  if (user.role === 'supervisor') {
    const defaults = { ver: true, crear: true, aprobar: true, rechazar: true };
    return defaults[action] === true;
  }
  // Operador por defecto
  if (user.role === 'operator') {
    const defaults = { ver: true, crear: true };
    return defaults[action] === true;
  }
  return false;
};

function PurchaseRequests() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Permisos del usuario
  const canCreate = userCan(user, 'notas_pedido', 'crear');
  const canApprove = userCan(user, 'notas_pedido', 'aprobar');
  const canReject = userCan(user, 'notas_pedido', 'rechazar');
  const canDelete = userCan(user, 'notas_pedido', 'eliminar');
  
  // Filtro especial desde Dashboard
  const statusFromUrl = searchParams.get('status') || '';
  
  const [requests, setRequests] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [ocBlockMsg, setOcBlockMsg] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [articleSelectOpen, setArticleSelectOpen] = useState(false);
  const { unitOf } = useArticleUnits();
  
  // Filtrar depósitos según permisos del usuario
  const allowedWarehouses = warehouses.filter(w => {
    if (user?.role === 'admin') return true;
    if (!user?.allowed_warehouses || user.allowed_warehouses.length === 0) return true;
    return user.allowed_warehouses.includes(w.warehouse_id);
  });
  
  const [formData, setFormData] = useState({
    warehouse_id: '',
    notes: '',
    priority: 'media',
    required_date: '',
    items: []
  });
  
  const [newItem, setNewItem] = useState({
    article_id: '',
    article_name: '',
    warehouse_id: '',
    quantity: 1,
    is_new_article: false,
    is_medicamento: false,
    unit: 'unidad'
  });
  
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Detección de posibles duplicados al cargar un "artículo nuevo".
  const dupMatches = useMemo(() => {
    if (!newItem.is_new_article) return { exact: [], similar: [] };
    const norm = (s) => (s || '').trim().toUpperCase().replace(/\s+/g, ' ');
    const q = norm(newItem.article_name);
    if (q.length < 3) return { exact: [], similar: [] };
    const exact = [], similar = [];
    for (const a of articles) {
      const n = norm(a.name);
      if (!n) continue;
      if (n === q) exact.push(a);
      else if (n.includes(q) || q.includes(n)) similar.push(a);
    }
    return { exact, similar: similar.slice(0, 5) };
  }, [newItem.is_new_article, newItem.article_name, articles]);
  
  // Filtros de búsqueda - inicializar con valor de URL si existe
  const [filters, setFilters] = useState({
    request_number: '',
    warehouse_name: '',
    created_by_name: '',
    article_name: '',  // v3.40.6 · busca en items[].article_name
    status: statusFromUrl
  });
  
  // Función para limpiar filtro especial
  const clearStatusFilter = () => {
    setFilters(prev => ({ ...prev, status: '' }));
    setSearchParams({});
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  // Prefill cuando llegamos desde Demanda Insatisfecha.
  // Espera a que `articles` esté cargado (race condition con fetchData async).
  const [linkedOpis, setLinkedOpis] = useState([]);
  useEffect(() => {
    if (searchParams.get('prefill') !== 'unmet') return;
    if (articles.length === 0) return; // articles aún cargando, reintenta cuando esté listo
    try {
      const raw = sessionStorage.getItem('np_prefill');
      if (raw) {
        const data = JSON.parse(raw);
        setFormData({
          warehouse_id: data.warehouse_id || '',
          notes: 'Generada desde Demanda Insatisfecha',
          priority: 'alta',
          required_date: '',
          items: (data.items || []).map(it => ({
            article_id: it.article_id,
            article_name: it.article_name,
            warehouse_id: data.warehouse_id,
            quantity: it.quantity,
            is_new_article: false,
          })),
        });
        setLinkedOpis(data.linked_internal_request_ids || []);
        // Popular el catálogo del selector "Agregar Artículo" — sino queda vacío
        // y el usuario no puede sumar artículos extras a la NP agrupada.
        setFilteredArticles(articles);
        setNewItem(prev => ({ ...prev, warehouse_id: data.warehouse_id || '' }));
        setDialogOpen(true);
        sessionStorage.removeItem('np_prefill');
        setSearchParams({});
        toast.info('Formulario precargado con la demanda insatisfecha');
      }
    } catch (e) {
      // ignore: prefill malformado o sessionStorage no disponible
      console.warn('np_prefill no aplicado:', e?.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles.length]);
  
  const fetchData = async () => {
    try {
      const [requestsRes, warehousesRes, articlesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-requests`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/articles?exclude_pending_review=true`, { withCredentials: true })
      ]);
      setRequests(requestsRes.data);
      setWarehouses(warehousesRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleWarehouseChange = (warehouseId) => {
    setFormData({ ...formData, warehouse_id: warehouseId, items: [] });
    // Modelo Catálogo: NO filtramos por warehouse. Mostramos TODO el catálogo.
    // El depósito de la NP define dónde se recibirán los artículos (destino),
    // no qué artículos se pueden pedir.
    setFilteredArticles(articles);
    setNewItem({ ...newItem, warehouse_id: warehouseId, article_id: '', article_name: '', is_medicamento: false, unit: 'unidad' });
  };
  
  const handleAddItem = () => {
    const qty = parseInt(newItem.quantity);
    const name = (newItem.article_name || '').trim();
    if (!name || !qty || qty < 1) {
      toast.error('Completa el artículo y la cantidad');
      return;
    }

    const item = {
      article_id: newItem.is_new_article ? null : newItem.article_id,
      article_name: newItem.is_new_article ? name.toUpperCase() : newItem.article_name,
      warehouse_id: formData.warehouse_id,
      quantity: qty,
      is_new_article: newItem.is_new_article,
      is_medicamento: newItem.is_new_article ? newItem.is_medicamento : null,
      unit: newItem.is_new_article ? (newItem.unit || 'unidad') : null
    };

    setFormData({ ...formData, items: [...formData.items, item] });
    setNewItem({
      article_id: '',
      article_name: '',
      warehouse_id: formData.warehouse_id,
      quantity: '',
      is_new_article: false,
      is_medicamento: false,
      unit: 'unidad'
    });
  };
  
  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Agrega al menos un artículo');
      return;
    }
    
    try {
      if (editingRequest) {
        await axios.put(`${BACKEND_URL}/api/purchase-requests/${editingRequest.request_id}`, formData, { withCredentials: true });
        toast.success('Nota de Pedido actualizada');
      } else {
        const payload = { ...formData };
        if (linkedOpis.length > 0) {
          payload.linked_internal_request_ids = linkedOpis;
        }
        await axios.post(`${BACKEND_URL}/api/purchase-requests`, payload, { withCredentials: true });
        toast.success('Nota de Pedido creada');
      }
      setDialogOpen(false);
      resetForm();
      setLinkedOpis([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar nota de pedido');
    }
  };
  
  const resetForm = () => {
    setFormData({ warehouse_id: '', notes: '', priority: 'media', required_date: '', items: [] });
    setNewItem({ article_id: '', article_name: '', warehouse_id: '', quantity: '', is_new_article: false, is_medicamento: false, unit: 'unidad' });
    setFilteredArticles([]);
    setEditingRequest(null);
  };
  
  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${selectedRequest.request_id}/approve`, 
        { items: selectedRequest.items, notes: selectedRequest.notes },
        { withCredentials: true }
      );
      toast.success('Nota de Pedido aprobada');
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    }
  };
  
  const handleReject = async (requestId) => {
    const reason = prompt('Motivo del rechazo:');
    if (reason === null) return;
    
    try {
      await axios.put(`${BACKEND_URL}/api/purchase-requests/${requestId}/reject`, 
        reason,  // Enviar como string simple en el body
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Nota de Pedido rechazada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    }
  };
  
  const handleDownloadPDF = (requestId, requestNumber) => {
    downloadPdf(`/purchase-requests/${requestId}/pdf`, requestNumber);
  };

  const handleDelete = async () => {
    const motivo = deleteReason.trim();
    if (!motivo) { toast.error('Indicá el motivo de la eliminación'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-requests/${deleteTarget.request_id}`, {
        data: { reason: motivo },
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(`Nota de Pedido ${deleteTarget.request_number} eliminada`);
      setDeleteTarget(null);
      setDeleteReason('');
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'No se pudo eliminar la Nota de Pedido';
      if (status === 409) {
        // Está incorporada en una OC → cartel emergente
        setDeleteTarget(null);
        setDeleteReason('');
        setOcBlockMsg(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setDeleting(false);
    }
  };
  
  const handleEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      warehouse_id: request.warehouse_id,
      notes: request.notes || '',
      priority: request.priority || 'media',
      required_date: request.required_date || '',
      items: request.items.map(it => ({
        article_id: it.article_id,
        article_name: it.article_name,
        warehouse_id: it.warehouse_id,
        quantity: it.quantity,
        is_new_article: it.is_new_article || false
      }))
    });
    const filtered = articles;
    setFilteredArticles(filtered);
    setDialogOpen(true);
  };
  
  const canEditRequest = (request) => {
    if (request.status !== 'pending') return false;
    if (user?.role === 'admin' || user?.role === 'supervisor') return true;
    return request.created_by === user?.user_id;
  };
  
  const priorityBadge = (priority) => {
    const map = {
      baja: { label: 'Baja', cls: 'bg-slate-100 text-slate-700' },
      media: { label: 'Media', cls: 'bg-blue-100 text-blue-800' },
      alta: { label: 'Alta', cls: 'bg-orange-100 text-orange-800' },
      urgente: { label: 'Urgente', cls: 'bg-red-100 text-red-800' }
    };
    const p = map[priority] || map.media;
    return <Badge className={p.cls}>{p.label}</Badge>;
  };
  
  const isOverdue = (request) => {
    if (!request.required_date || request.status !== 'pending') return false;
    return new Date(request.required_date) < new Date();
  };
  
  const openApproveDialog = (request) => {
    setSelectedRequest({ ...request });
    setApproveDialogOpen(true);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: { variant: 'secondary', label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
      approved: { variant: 'default', label: 'Aprobada', className: 'bg-green-100 text-green-800' },
      rejected: { variant: 'destructive', label: 'Rechazada', className: 'bg-red-100 text-red-800' }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={style.className}>{style.label}</Badge>;
  };
  
  // Filtrar requests según los filtros
  const filteredRequests = requests.filter(request => {
    // v3.40.6 — filtro por artículo: busca en items[].article_name (case-insensitive, substring)
    const articleQ = filters.article_name.trim().toLowerCase();
    const matchesArticle = articleQ === '' || (request.items || []).some(it =>
      (it.article_name || '').toLowerCase().includes(articleQ)
    );
    return (
      (filters.request_number === '' || request.request_number?.toLowerCase().includes(filters.request_number.toLowerCase())) &&
      (filters.warehouse_name === '' || request.warehouse_name?.toLowerCase().includes(filters.warehouse_name.toLowerCase())) &&
      (filters.created_by_name === '' || request.created_by_name?.toLowerCase().includes(filters.created_by_name.toLowerCase())) &&
      (filters.status === '' || request.status === filters.status) &&
      matchesArticle
    );
  });
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="purchase-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Notas de Pedido</h1>
          <p className="text-muted-foreground">Solicita artículos para tu depósito</p>
          
          {/* Indicador de filtro activo */}
          {statusFromUrl && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-blue-500 text-white flex items-center gap-1">
                <ClipboardList className="w-3 h-3" />
                Mostrando solo: {statusFromUrl === 'pending' ? 'Pendientes' : statusFromUrl === 'approved' ? 'Aprobadas' : statusFromUrl === 'rejected' ? 'Rechazadas' : statusFromUrl}
                <button onClick={clearStatusFilter} className="ml-1 hover:bg-blue-600 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            </div>
          )}
        </div>
        
        {canCreate && (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-request-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Nota de Pedido
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRequest ? 'Editar Nota de Pedido' : 'Nueva Nota de Pedido'}</DialogTitle>
              <DialogDescription>{editingRequest ? `Edición de ${editingRequest.request_number}` : 'Solicita artículos para un depósito'}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="warehouse">Depósito Destino *</Label>
                <select
                  id="warehouse"
                  value={formData.warehouse_id}
                  onChange={(e) => handleWarehouseChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                >
                  <option value="">Selecciona depósito</option>
                  {allowedWarehouses.map(w => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                  ))}
                </select>
              </div>
              
              {formData.warehouse_id && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium">Agregar Artículos</h4>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_new"
                      checked={newItem.is_new_article}
                      onChange={(e) => setNewItem({ 
                        ...newItem, 
                        is_new_article: e.target.checked,
                        article_id: '',
                        article_name: '',
                        is_medicamento: false,
                        unit: 'unidad'
                      })}
                      data-testid="np-is-new-checkbox"
                    />
                    <Label htmlFor="is_new" className="text-sm">Artículo nuevo (no existe en sistema)</Label>
                  </div>
                  
                  {newItem.is_new_article ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="NOMBRE DEL ARTÍCULO NUEVO"
                        value={newItem.article_name}
                        onChange={(e) => setNewItem({ ...newItem, article_name: e.target.value.toUpperCase() })}
                        className="uppercase"
                        data-testid="np-new-article-name"
                      />
                      {(dupMatches.exact.length > 0 || dupMatches.similar.length > 0) && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-sm" data-testid="np-duplicate-warning">
                          <div className="flex items-center gap-2 text-amber-800 font-medium">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            {dupMatches.exact.length > 0
                              ? 'Este artículo ya existe en el catálogo'
                              : 'Puede que ya exista un artículo similar'}
                          </div>
                          <p className="text-xs text-amber-700 mt-0.5">Para no duplicarlo, seleccioná el existente:</p>
                          <div className="mt-1.5 space-y-1">
                            {[...dupMatches.exact, ...dupMatches.similar].slice(0, 6).map(a => (
                              <div key={a.article_id} className="flex items-center justify-between gap-2 bg-white/60 rounded px-2 py-1">
                                <span className="flex items-center gap-1.5 min-w-0">
                                  {a.is_medicamento ? <Pill className="w-3.5 h-3.5 text-teal-600 shrink-0" /> : null}
                                  <span className="truncate">{a.name}</span>
                                  <span className="text-xs text-muted-foreground shrink-0">(stock: {a.stock} {unitOf(a.article_id)})</span>
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs shrink-0"
                                  data-testid={`np-use-existing-${a.article_id}`}
                                  onClick={() => setNewItem({ ...newItem, is_new_article: false, article_id: a.article_id, article_name: a.name, is_medicamento: false, unit: 'unidad' })}
                                >
                                  Usar este
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">¿Es medicamento?</Label>
                          <select
                            value={newItem.is_medicamento ? 'si' : 'no'}
                            onChange={(e) => setNewItem({ ...newItem, is_medicamento: e.target.value === 'si' })}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid="np-new-article-ismed"
                          >
                            <option value="no">📦 Insumo</option>
                            <option value="si">💊 Medicamento</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Unidad de medida</Label>
                          <select
                            value={newItem.unit}
                            onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid="np-new-article-unit"
                          >
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Cantidad</Label>
                          <Input
                            type="number"
                            placeholder="Cant."
                            min="1"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value === '' ? '' : (parseInt(e.target.value) || '') })}
                            data-testid="np-new-article-qty"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-start">
                      <Popover open={articleSelectOpen} onOpenChange={setArticleSelectOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            className="flex-1 justify-between font-normal"
                            data-testid="np-article-select"
                          >
                            {newItem.article_id
                              ? (filteredArticles.find(a => a.article_id === newItem.article_id)?.name || 'Artículo')
                              : <span className="text-muted-foreground">Buscar artículo por nombre...</span>}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                            <CommandInput placeholder="Escribí el nombre del artículo..." data-testid="np-article-search" />
                            <CommandList>
                              <CommandEmpty>Sin resultados.</CommandEmpty>
                              <CommandGroup>
                                {filteredArticles.map(a => (
                                  <CommandItem
                                    key={a.article_id}
                                    value={`${a.name} ${a.category || ''}`}
                                    onSelect={() => { setNewItem({ ...newItem, article_id: a.article_id, article_name: a.name }); setArticleSelectOpen(false); }}
                                    data-testid={`np-article-option-${a.article_id}`}
                                    className="flex items-center justify-between gap-2"
                                  >
                                    <span className="flex items-center gap-2">
                                      {a.is_medicamento && <Pill className="w-3.5 h-3.5 text-teal-600 shrink-0" />}
                                      <span>{a.name}</span>
                                    </span>
                                    <span className="flex items-center gap-2 shrink-0">
                                      {a.category && <Badge variant="outline" className="text-[10px]">{a.category}</Badge>}
                                      <span className="text-xs text-muted-foreground">stock: {a.stock} {unitOf(a.article_id)}</span>
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="number"
                        placeholder="Cant."
                        min="1"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value === '' ? '' : (parseInt(e.target.value) || '') })}
                        className="w-24"
                        data-testid="np-existing-qty"
                      />
                    </div>
                  )}
                  
                  <Button type="button" variant="outline" onClick={handleAddItem} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Agregar a la lista
                  </Button>
                  
                  {formData.items.length > 0 && (
                    <div className="border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Artículo</TableHead>
                            <TableHead className="text-center">Cantidad</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {item.article_name}
                                {item.is_new_article && (
                                  <>
                                    <Badge className="ml-2 bg-blue-100 text-blue-800">NUEVO</Badge>
                                    <Badge variant="outline" className="ml-1 text-[10px]">{item.is_medicamento ? '💊 Medicamento' : '📦 Insumo'}</Badge>
                                  </>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {item.quantity} <span className="text-xs text-muted-foreground">{item.is_new_article ? (item.unit || 'unidad') : unitOf(item.article_id)}</span>
                              </TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveItem(index)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <Label htmlFor="notes">Observaciones</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="priority">Prioridad *</Label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="np-priority-select"
                  >
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="required_date">Fecha Requerida</Label>
                  <Input
                    id="required_date"
                    type="date"
                    value={formData.required_date}
                    onChange={(e) => setFormData({ ...formData, required_date: e.target.value })}
                    data-testid="np-required-date"
                  />
                </div>
              </div>
              
              {editingRequest && (
                <div className="border-t pt-4">
                  <Attachments
                    entityType="purchase_request"
                    entityId={editingRequest.request_id}
                    currentUser={user}
                  />
                </div>
              )}
              
              <DialogFooter>
                <Button type="submit" disabled={formData.items.length === 0} data-testid="np-submit-btn">
                  {editingRequest ? 'Guardar Cambios' : 'Crear Nota de Pedido'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Input
                    placeholder="Filtrar número..."
                    value={filters.request_number}
                    onChange={(e) => setFilters({ ...filters, request_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar depósito..."
                    value={filters.warehouse_name}
                    onChange={(e) => setFilters({ ...filters, warehouse_name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar solicitante..."
                    value={filters.created_by_name}
                    onChange={(e) => setFilters({ ...filters, created_by_name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <Input
                    data-testid="filter-article-name"
                    placeholder="Filtrar artículo..."
                    value={filters.article_name}
                    onChange={(e) => setFilters({ ...filters, article_name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead>Fecha Req.</TableHead>
                <TableHead>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="">Todos</option>
                    <option value="pending">Pendiente</option>
                    <option value="approved">Aprobada</option>
                    <option value="rejected">Rechazada</option>
                  </select>
                </TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.request_id}>
                  <TableCell className="font-mono font-medium">{request.request_number}</TableCell>
                  <TableCell>{fmtDate(request.created_at)}</TableCell>
                  <TableCell>{request.warehouse_name}</TableCell>
                  <TableCell>{request.created_by_name}</TableCell>
                  <TableCell>
                    {filters.article_name.trim() ? (
                      <div className="flex flex-col gap-0.5">
                        {(request.items || [])
                          .filter(it => (it.article_name || '').toLowerCase().includes(filters.article_name.trim().toLowerCase()))
                          .map((it, i) => (
                            <span key={i} className="text-xs">
                              <span className="font-medium">{it.article_name}</span>
                              <span className="text-muted-foreground"> · {it.quantity}</span>
                            </span>
                          ))}
                      </div>
                    ) : (
                      `${request.items?.length || 0} artículos`
                    )}
                  </TableCell>
                  <TableCell>{priorityBadge(request.priority)}</TableCell>
                  <TableCell>
                    {request.required_date ? (
                      <span className={isOverdue(request) ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                        {isOverdue(request) && <AlertTriangle className="w-3 h-3" />}
                        {fmtDate(request.required_date)}
                      </span>
                    ) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>{getStatusBadge(request.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => { setSelectedRequest(request); setDetailDialogOpen(true); }} 
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(request.request_id, request.request_number)} title="Descargar PDF">
                        <FileText className="w-4 h-4" />
                      </Button>
                      
                      {canEditRequest(request) && (
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(request)} title="Editar NP" data-testid={`edit-np-${request.request_id}`}>
                          <Pencil className="w-4 h-4 text-blue-600" />
                        </Button>
                      )}
                      {canApprove && request.status === 'pending' && (
                        <Button variant="ghost" size="icon" onClick={() => openApproveDialog(request)} title="Aprobar/Modificar">
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                      )}
                      {canReject && request.status === 'pending' && (
                        <Button variant="ghost" size="icon" onClick={() => handleReject(request.request_id)} title="Rechazar">
                          <X className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="icon" onClick={() => { setDeleteTarget(request); setDeleteReason(''); }} title="Eliminar NP" data-testid={`delete-np-${request.request_id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredRequests.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay notas de pedido que coincidan con los filtros.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog de Aprobación */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aprobar Nota de Pedido</DialogTitle>
            <DialogDescription>
              {selectedRequest?.request_number} - Puedes modificar los items antes de aprobar
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-center">Cantidad</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRequest.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.is_new_article ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={item.article_name}
                                onChange={(e) => {
                                  const newItems = [...selectedRequest.items];
                                  newItems[index].article_name = e.target.value;
                                  setSelectedRequest({ ...selectedRequest, items: newItems });
                                }}
                                className="max-w-[200px]"
                                placeholder="Nombre del artículo"
                              />
                              <Badge className="bg-blue-100 text-blue-800 shrink-0">NUEVO</Badge>
                            </div>
                          ) : (
                            item.article_name
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...selectedRequest.items];
                              newItems[index].quantity = parseInt(e.target.value) || 1;
                              setSelectedRequest({ ...selectedRequest, items: newItems });
                            }}
                            className="w-20 text-center mx-auto"
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => {
                              const newItems = selectedRequest.items.filter((_, i) => i !== index);
                              setSelectedRequest({ ...selectedRequest, items: newItems });
                            }}
                            title="Eliminar artículo"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <div>
                <Label>Observaciones</Label>
                <Textarea
                  value={selectedRequest.notes || ''}
                  onChange={(e) => setSelectedRequest({ ...selectedRequest, notes: e.target.value })}
                  rows={2}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancelar</Button>
                <Button 
                  onClick={handleApprove} 
                  className="bg-green-600 hover:bg-green-700"
                  disabled={selectedRequest.items?.length === 0}
                >
                  <Check className="w-4 h-4 mr-2" /> Aprobar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog de Ver Detalle */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Nota de Pedido</DialogTitle>
            <DialogDescription>{selectedRequest?.request_number}</DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Solicitado por</p>
                  <p className="font-medium">{selectedRequest.created_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha</p>
                  <p className="font-medium">{fmtDate(selectedRequest.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Depósito Destino</p>
                  <p className="font-medium">{selectedRequest.warehouse_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <p className="font-medium">{getStatusBadge(selectedRequest.status)}</p>
                </div>
                {selectedRequest.approved_by_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Aprobado por</p>
                    <p className="font-medium">{selectedRequest.approved_by_name}</p>
                  </div>
                )}
                {selectedRequest.rejected_by_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Rechazado por</p>
                    <p className="font-medium">{selectedRequest.rejected_by_name}</p>
                  </div>
                )}
              </div>
              
              {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Motivo del rechazo:</p>
                  <p className="text-red-700">{selectedRequest.rejection_reason}</p>
                </div>
              )}
              
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-center">Cantidad</TableHead>
                      <TableHead className="text-center">Unidad</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRequest.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.article_name}
                          {item.is_new_article && (
                            <Badge className="ml-2 bg-blue-100 text-blue-800">NUEVO</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">{item.quantity}</TableCell>
                        <TableCell className="text-center text-muted-foreground">{item.is_new_article ? (item.unit || 'unidad') : unitOf(item.article_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {selectedRequest.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Observaciones</p>
                  <p className="bg-slate-50 p-3 rounded">{selectedRequest.notes}</p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <Attachments
                  entityType="purchase_request"
                  entityId={selectedRequest.request_id}
                  currentUser={user}
                  canUpload={selectedRequest.status === 'pending' && (user?.role === 'admin' || user?.role === 'supervisor' || selectedRequest.created_by === user?.user_id)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Eliminar NP: diálogo con motivo obligatorio */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(''); } }}>
        <DialogContent data-testid="delete-np-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" /> Eliminar Nota de Pedido
            </DialogTitle>
            <DialogDescription>
              Vas a eliminar la NP <strong>{deleteTarget?.request_number}</strong>. Esta acción no se puede deshacer y quedará registrada en Auditoría.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-reason">Motivo de la eliminación *</Label>
            <Textarea
              id="delete-reason"
              placeholder="Indicá por qué se elimina esta Nota de Pedido..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
              data-testid="delete-np-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteReason(''); }} data-testid="delete-np-cancel">Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting || !deleteReason.trim()} data-testid="delete-np-confirm">
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cartel emergente: NP afectada a una OC */}
      <AlertDialog open={!!ocBlockMsg} onOpenChange={(o) => { if (!o) setOcBlockMsg(''); }}>
        <AlertDialogContent data-testid="np-oc-blocked-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> No se puede eliminar
            </AlertDialogTitle>
            <AlertDialogDescription>{ocBlockMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setOcBlockMsg('')} data-testid="np-oc-blocked-ok">Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PurchaseRequests;
